import { graphqlFetch } from '@pylot-data/graphqlFetch'
import type { CategoryTree, Maybe } from '@pylot-data/pylotschema'
import {
    ESPACIO_CASA_URL_KEY,
    getEspaciosCasaCategoriesQuery
} from './graphql/getEspaciosCasaCategoriesQuery'

export type EspacioCasaItem = {
    name: string
    href: string
    image?: string
    position: number
}

/** Magento expone `menu_icon` en CategoryTree (no está en el schema TS base). */
type CategoryTreeWithMenuIcon = CategoryTree & {
    menu_icon?: Maybe<string>
}

const getCmsGatewayUrl = (): string | undefined =>
    process.env.CMS_API_GATEWAY_URL ||
    process.env.NEXT_PUBLIC_CMS_API_GATEWAY_URL ||
    undefined

const getMagentoMediaOrigin = (): string | undefined => {
    if (process.env.MAGENTO_MEDIA_ORIGIN)
        return process.env.MAGENTO_MEDIA_ORIGIN
    const gateway = getCmsGatewayUrl()
    if (!gateway) return undefined
    try {
        return new URL(gateway).origin
    } catch {
        return undefined
    }
}

/**
 * Magento GraphQL resuelve `image` con el base URL de la store (prod).
 * En integration el archivo vive en el host de CMS, no en casaideas.cl.
 * `menu_icon` suele venir relativo (`/media/catalog/category/...`).
 */
export const resolveCategoryImageUrl = (
    image?: string | null,
    mediaOrigin = getMagentoMediaOrigin()
): string | undefined => {
    if (!image) return undefined

    if (!mediaOrigin) {
        if (image.startsWith('http') || image.startsWith('//')) return image
        return undefined
    }

    try {
        const absolute = image.startsWith('//')
            ? `https:${image}`
            : image.startsWith('http')
              ? image
              : new URL(image, mediaOrigin).href
        const url = new URL(absolute)
        if (!url.pathname.includes('/media/')) return url.href
        return `${mediaOrigin}${url.pathname}${url.search}`
    } catch {
        return image
    }
}

const toHref = (child: CategoryTree): string => {
    if (child.external_url) return child.external_url
    if (child.url_path) return `/categoria/${child.url_path}`
    return '/'
}

/** Slider home: Category Icon. Mega menú header: Category Menu Image. */
export type EspaciosCasaImageKind = 'icon' | 'menu'

const pickEspaciosCasaImage = (
    child: CategoryTree,
    imageKind: EspaciosCasaImageKind
): string | undefined => {
    const withIcon = child as CategoryTreeWithMenuIcon
    if (imageKind === 'menu') {
        return resolveCategoryImageUrl(child.megamenu_image)
    }
    return resolveCategoryImageUrl(child.megamenu_icon || withIcon.menu_icon)
}

const MENU_SLIDER_SEGMENT = '/menu-slider-espacio-de-la-casa/'

/**
 * Muebles y Comedor tienen el PNG en la carpeta del menú, pero Magento
 * no trae `megamenu_image`. Si un hermano sí lo trae, se arma la misma ruta.
 */
const withMenuSliderFallback = (
    items: EspacioCasaItem[]
): EspacioCasaItem[] => {
    const sample = items.find((item) =>
        item.image?.includes(MENU_SLIDER_SEGMENT)
    )?.image
    if (!sample) return items
    const dir = sample.slice(0, sample.lastIndexOf('/') + 1)
    return items.map((item) =>
        item.image ? item : { ...item, image: `${dir}${item.name}.png` }
    )
}

export const mapEspaciosCasaItems = (
    children?: Maybe<Array<Maybe<CategoryTree>>> | null,
    imageKind: EspaciosCasaImageKind = 'icon'
): EspacioCasaItem[] => {
    const items = (children ?? [])
        .filter((child): child is CategoryTree => Boolean(child?.name))
        .map((child) => {
            const image = pickEspaciosCasaImage(child, imageKind)
            return {
                name: String(child.name),
                href: toHref(child),
                ...(image ? { image } : {}),
                position: child.position ?? 0
            }
        })
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))

    return imageKind === 'menu' ? withMenuSliderFallback(items) : items
}

async function getEspaciosCasaCategories({
    locale = '',
    urlKey = ESPACIO_CASA_URL_KEY,
    imageKind = 'icon'
}: {
    locale?: string
    urlKey?: string
    imageKind?: EspaciosCasaImageKind
} = {}): Promise<EspacioCasaItem[]> {
    try {
        const { data } = await graphqlFetch<
            { urlKey: string },
            { categories: { items: Maybe<Array<Maybe<CategoryTree>>> } }
        >({
            query: getEspaciosCasaCategoriesQuery,
            variables: { urlKey },
            locale,
            customURL: getCmsGatewayUrl(),
            fetchOptions: {
                method: 'POST',
                cache: 'no-store'
            }
        })

        const root = data?.categories?.items?.[0]
        return mapEspaciosCasaItems(root?.children, imageKind)
    } catch (error) {
        console.error('getEspaciosCasaCategories', error)
        return []
    }
}

export default getEspaciosCasaCategories
